/* TBHNP cart rewards runtime v7 */
(() => {
  const VERSION = '2026-09-04.1';
  const previous = window.TBHNPCartRewards;
  if (previous?.version === VERSION) return;

  const REWARD_VARIANT_IDS = new Set(['53776651026616', '53972593148088']);
  const FREE_TEE_SIZES = new Set(['S', 'M', 'L', 'XL']);

  const isFullCart = (cart) =>
    Boolean(
      cart &&
        Array.isArray(cart.items) &&
        Number.isFinite(Number(cart.item_count)) &&
        Number.isFinite(Number(cart.total_price)) &&
        typeof cart.currency === 'string' &&
        cart.currency.length === 3
    );

  const qualifyingTotal = (cart) =>
    (cart?.items || []).reduce((total, item) => {
      if (item.properties?._free_tee_reward === 'true') return total;
      if (item.properties?._mystery_gift_reward === 'true') return total;
      if (REWARD_VARIANT_IDS.has(String(item.variant_id || item.id || ''))) return total;
      return total + Number(item.original_line_price || 0);
    }, 0);

  const qualifyingQuantity = (cart) =>
    (cart?.items || []).reduce((quantity, item) => {
      if (item.properties?._free_tee_reward === 'true') return quantity;
      if (item.properties?._mystery_gift_reward === 'true') return quantity;
      if (REWARD_VARIANT_IDS.has(String(item.variant_id || item.id || ''))) return quantity;
      return quantity + Number(item.quantity || 0);
    }, 0);

  const hasValidFreeTeeSize = (item) =>
    Boolean(
      item &&
        item.properties?._free_tee_reward === 'true' &&
        FREE_TEE_SIZES.has(String(item.properties?.Size || '').toUpperCase())
    );

  const ceilDecimalProduct = (value, rate) => {
    const integerValue = Number(value);
    const rateText = String(rate ?? '').trim();
    const match = rateText.match(/^(\d+)(?:\.(\d+))?$/);
    if (!Number.isSafeInteger(integerValue) || integerValue < 0 || !match) return null;

    const fraction = match[2] || '';
    const scale = 10n ** BigInt(fraction.length);
    const rateUnits = BigInt(`${match[1]}${fraction}`);
    const product = BigInt(integerValue) * rateUnits;
    const result = Number((product + scale - 1n) / scale);
    return Number.isSafeInteger(result) ? result : null;
  };

  const rewardState = (cart, unlocked) => {
    const rewardItems = (cart?.items || []).filter(
      (item) => item.properties?._free_tee_reward === 'true'
    );
    const selectedReward = unlocked
      ? rewardItems.find(
          (item) =>
            Number(item.quantity) === 1 &&
            Number(item.final_line_price) === 0 &&
            hasValidFreeTeeSize(item)
        )
      : null;

    return {
      rewardItems,
      selectedReward,
      invalidRewardItems: rewardItems.filter((item) => item !== selectedReward),
    };
  };

  const checkoutBlocks = previous?._checkoutBlocks || new Set();
  const currencyRates = previous?.currencyRates || Object.create(null);
  const hadPreviousRuntime = Boolean(previous);

  const runtime = {
    version: VERSION,
    snapshot: isFullCart(previous?.snapshot) ? previous.snapshot : null,
    revision: Number(previous?.revision) || 0,
    request: null,
    requestController: null,
    requestToken: Number(previous?.requestToken) || 0,
    rewardCleanup: null,
    rewardSelection: null,
    mutation: previous?.mutation || Promise.resolve(),
    freeTeeProductsHTML: previous?.freeTeeProductsHTML,
    currencyRates,
    _checkoutBlocks: checkoutBlocks,
    _checkoutGuardsInstalled: Boolean(previous?._checkoutGuardsInstalled || hadPreviousRuntime),

    isFullCart,
    qualifyingTotal,
    qualifyingQuantity,
    hasValidFreeTeeSize,
    validFreeTeeSizes: FREE_TEE_SIZES,
    ceilDecimalProduct,
    rewardState,

    currencyRate(shopCurrency, presentmentCurrency, providedRate, providedCurrency) {
      const requestedCurrency = String(
        presentmentCurrency || window.Shopify?.currency?.active || shopCurrency || ''
      ).toUpperCase();
      const baseCurrency = String(shopCurrency || '').toUpperCase();

      if (!baseCurrency || !requestedCurrency) return null;
      if (requestedCurrency === baseCurrency) return 1;

      const key = `${baseCurrency}:${requestedCurrency}`;
      const activeCurrency = String(window.Shopify?.currency?.active || '').toUpperCase();
      const runtimeRate = Number(window.Shopify?.currency?.rate);
      if (
        activeCurrency === requestedCurrency &&
        Number.isFinite(runtimeRate) &&
        runtimeRate > 0
      ) {
        this.currencyRates[key] = runtimeRate;
        return runtimeRate;
      }

      const fallbackRate = Number(providedRate);
      const fallbackCurrency = String(providedCurrency || '').toUpperCase();
      if (
        fallbackCurrency === requestedCurrency &&
        Number.isFinite(fallbackRate) &&
        fallbackRate > 0
      ) {
        this.currencyRates[key] = fallbackRate;
        return fallbackRate;
      }

      const cachedRate = Number(this.currencyRates[key]);
      return Number.isFinite(cachedRate) && cachedRate > 0 ? cachedRate : null;
    },

    convertedThreshold(
      value,
      shopCurrency,
      presentmentCurrency,
      providedRate,
      providedCurrency
    ) {
      const rate = this.currencyRate(
        shopCurrency,
        presentmentCurrency,
        providedRate,
        providedCurrency
      );
      return rate === null ? null : ceilDecimalProduct(value, rate);
    },

    setCheckoutBlocked(owner, blocked) {
      if (blocked) {
        checkoutBlocks.add(owner);
      } else {
        checkoutBlocks.delete(owner);
      }
      if (typeof document !== 'undefined') {
        const checkoutBlocked = checkoutBlocks.size > 0;
        document.documentElement.classList.toggle('free-tee-reward-cleanup', checkoutBlocked);
        document
          .querySelectorAll(
            '[name="checkout"], .additional-checkout-buttons, shopify-accelerated-checkout-cart'
          )
          .forEach((control) => {
            if (checkoutBlocked) {
              if (!control.hasAttribute('data-tbhnp-checkout-blocked')) {
                control.dataset.tbhnpPreviousAriaDisabled = control.getAttribute('aria-disabled') ?? '';
              }
              control.dataset.tbhnpCheckoutBlocked = 'true';
              control.setAttribute('aria-disabled', 'true');
            } else if (control.hasAttribute('data-tbhnp-checkout-blocked')) {
              const previous = control.dataset.tbhnpPreviousAriaDisabled;
              if (previous) control.setAttribute('aria-disabled', previous);
              else control.removeAttribute('aria-disabled');
              delete control.dataset.tbhnpPreviousAriaDisabled;
              delete control.dataset.tbhnpCheckoutBlocked;
            }
          });
      }
    },

    isCheckoutBlocked() {
      return checkoutBlocks.size > 0;
    },

    set(cart) {
      if (!isFullCart(cart)) return null;
      this.snapshot = cart;
      this.revision += 1;
      return cart;
    },

    invalidate() {
      this.snapshot = null;
      this.revision += 1;
    },

    async get(force = false) {
      if (!force && isFullCart(this.snapshot)) return this.snapshot;
      if (!force && this.request) return this.request;

      if (force && this.requestController) {
        this.requestController.abort();
      }
      if (force) this.invalidate();

      const token = ++this.requestToken;
      const startingRevision = this.revision;
      const controller =
        typeof AbortController === 'function' ? new AbortController() : null;
      this.requestController = controller;
      const root = window.Shopify?.routes?.root || '/';
      const options = {
        credentials: 'same-origin',
        headers: {Accept: 'application/json'},
      };
      if (controller) options.signal = controller.signal;

      const request = fetch(`${root}cart.js`, options)
        .then((response) => {
          if (!response.ok) throw new Error('Unable to refresh cart');
          return response.json();
        })
        .then((cart) => {
          if (token !== this.requestToken) {
            if (isFullCart(this.snapshot)) return this.snapshot;
            if (this.request && this.request !== request) return this.request;
            return cart;
          }

          if (this.revision !== startingRevision && isFullCart(this.snapshot)) {
            return this.snapshot;
          }

          const accepted = this.set(cart);
          if (!accepted) throw new Error('Incomplete cart response');
          return accepted;
        })
        .catch((error) => {
          if (token !== this.requestToken) {
            if (isFullCart(this.snapshot)) return this.snapshot;
            if (this.request && this.request !== request) return this.request;
          }
          throw error;
        })
        .finally(() => {
          if (token !== this.requestToken) return;
          this.request = null;
          this.requestController = null;
        });

      this.request = request;
      return request;
    },

    async resolve(candidate) {
      const accepted = this.set(candidate);
      if (accepted) return accepted;
      this.invalidate();
      return this.get();
    },

    enqueue(task) {
      const next = this.mutation.catch(() => {}).then(() => {
        this.invalidate();
        return task();
      });
      this.mutation = next.catch(() => {});
      return next;
    },

    cleanupRewards(task) {
      if (this.rewardCleanup) return this.rewardCleanup;
      const cleanup = this.enqueue(task).finally(() => {
        this.rewardCleanup = null;
      });
      this.rewardCleanup = cleanup;
      return cleanup;
    },

    selectReward(task) {
      if (this.rewardSelection) return this.rewardSelection;
      const selection = this.enqueue(task).finally(() => {
        if (this.rewardSelection === selection) this.rewardSelection = null;
      });
      this.rewardSelection = selection;
      return selection;
    },
  };

  window.TBHNPCartRewards = runtime;

  const isCheckoutControl = (target) =>
    target instanceof Element &&
    Boolean(
      target.closest(
        '[name="checkout"], .additional-checkout-buttons, shopify-accelerated-checkout-cart'
      )
    );

  const isCheckoutForm = (target) =>
    target instanceof HTMLFormElement &&
    Boolean(
      target.matches('form[action*="/cart"]') ||
        target.querySelector(
          '[name="checkout"], .additional-checkout-buttons, shopify-accelerated-checkout-cart'
        )
    );

  if (typeof document !== 'undefined' && !runtime._checkoutGuardsInstalled) {
    document.addEventListener(
      'click',
      (event) => {
        if (!window.TBHNPCartRewards.isCheckoutBlocked()) return;
        if (!isCheckoutControl(event.target)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );

    document.addEventListener(
      'submit',
      (event) => {
        if (!window.TBHNPCartRewards.isCheckoutBlocked()) return;
        if (!isCheckoutControl(event.submitter) && !isCheckoutForm(event.target)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );

    runtime._checkoutGuardsInstalled = true;
  }
})();
